import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import {
  CheckCircle,
  ArrowRight,
  Loader2,
  Building2,
  User,
  CreditCard,
  Rocket,
} from "lucide-react";

function Onboarding({ onOnboardingComplete, onNavigateToLogin }) {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [onboardingData, setOnboardingData] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    // Obtener el email desde la URL
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    if (!email) {
      window.location.href = '/registro-constructora';
      return;
    }
    // Consultar al backend los datos del usuario y constructora
    const fetchOnboardingData = async () => {
      try {
        const response = await fetch(`https://nokk-backend.vercel.app/api/usuario-onboarding?email=${encodeURIComponent(email)}`);
        const result = await response.json();
        if (result.success) {
          setOnboardingData({
            usuario: result.usuario,
            constructora: result.constructora
          });
        } else {
          alert('No se encontraron datos para el onboarding. Por favor, contacta soporte.');
          window.location.href = '/registro-constructora';
        }
      } catch (error) {
        alert('Error consultando datos de onboarding: ' + error.message);
        window.location.href = '/registro-constructora';
      }
    };
    fetchOnboardingData();
  }, []);

  // Auto-advance steps
  useEffect(() => {
    if (step < 4) {
      const timer = setTimeout(() => {
        setStep(step + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // ✅ Función para verificar estructura de tablas
  const verificarEstructuraTablas = async () => {
    try {
      console.log("🔍 Verificando estructura de tablas...");
      
      // Verificar tabla constructoras
      const { data: constructoras, error: constructoraError } = await supabase
        .from("constructoras")
        .select("*")
        .limit(1);
      
      if (constructoraError) {
        console.error("❌ Error tabla constructoras:", constructoraError);
        return false;
      }
      
      console.log("✅ Tabla constructoras accesible");
      console.log("📋 Columnas constructoras:", Object.keys(constructoras[0] || {}));
      
      // Verificar tabla usuarios
      const { data: usuarios, error: usuarioError } = await supabase
        .from("usuarios")
        .select("*")
        .limit(1);
      
      if (usuarioError) {
        console.error("❌ Error tabla usuarios:", usuarioError);
        return false;
      }
      
      console.log("✅ Tabla usuarios accesible");
      console.log("📋 Columnas usuarios:", Object.keys(usuarios[0] || {}));
      
      return true;
    } catch (error) {
      console.error("❌ Error verificando estructura:", error);
      return false;
    }
  };

  const completarOnboarding = async () => {
    setLoading(true);

    try {
      console.log("🚀 Completando onboarding...");

      // ✅ Los datos ya existen, solo mostrar el onboarding
      console.log("📝 Datos de onboarding:", onboardingData);

      // ✅ Preparar datos para el login exitoso usando los datos que ya existen
      console.log("📝 Preparando datos de login...");
      const datosLogin = {
        usuario: {
          id: onboardingData.usuario.id,
          email: onboardingData.usuario.email,
          nombre: onboardingData.usuario.nombre,
          apellido: onboardingData.usuario.apellido,
          tipo_usuario: onboardingData.usuario.tipo_usuario,
          constructora_id: onboardingData.usuario.constructora_id,
        },
        constructora: {
          id: onboardingData.constructora.id,
          nombre: onboardingData.constructora.nombre,
          email: onboardingData.constructora.email,
          cuit: onboardingData.constructora.cuit,
          logo_url: onboardingData.constructora.logo_url,
        },
      };

      console.log("✅ Datos de login preparados:", datosLogin);

      // ✅ Limpiar sessionStorage
      sessionStorage.removeItem("registro_constructora");

      // ✅ Completar onboarding
      console.log("🚀 Completando onboarding...");
      onOnboardingComplete(datosLogin);
    } catch (error) {
      console.error("❌ Error en onboarding:", error);
      console.error("❌ Stack trace:", error.stack);
      alert("Error durante el onboarding: " + error.message);
      setLoading(false);
    }
  };

  const steps = [
    {
      icon: CheckCircle,
      title: "¡Pago Confirmado!",
      description: "Tu suscripción ha sido activada exitosamente",
    },
    {
      icon: Building2,
      title: "Configurando Empresa",
      description: "Creando la estructura de tu constructora",
    },
    {
      icon: User,
      title: "Creando Usuario",
      description: "Configurando tu acceso administrativo",
    },
    {
      icon: Rocket,
      title: "¡Todo Listo!",
      description: "Tu sistema está configurado y listo para usar",
    },
  ];

  return (
    <>
      <style jsx global>{`
        input,
        select,
        textarea,
        button {
          font-size: 16px !important;
          -webkit-text-size-adjust: none;
          -webkit-appearance: none;
          -webkit-border-radius: 0;
          touch-action: manipulation;
        }

        .onboarding-container {
          height: 100vh;
          height: 100dvh;
        }

        @supports (-webkit-touch-callout: none) {
          .onboarding-container {
            height: -webkit-fill-available;
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .fade-in-up {
          animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .pulse {
          animation: pulse 2s ease-in-out infinite;
        }

        .nav-button-next:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
        }

        .nav-button-next:active {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .nav-button-next:disabled:hover {
          transform: none;
          box-shadow: 0 4px 12px rgba(148, 163, 184, 0.3);
        }

        @keyframes finalizeGlow {
          0%, 100% {
            text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
          }
          50% {
            text-shadow: 0 0 15px rgba(255, 255, 255, 0.8), 0 0 25px rgba(255, 255, 255, 0.4);
          }
        }

        .finalize-text {
          animation: finalizeGlow 2s ease-in-out infinite;
        }
      `}</style>

      <div
        className="onboarding-container"
        style={{
          background: isMobile
            ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
            : "linear-gradient(135deg, #fafbfc 0%, #f1f5f9 25%, #e2e8f0 50%, #cbd5e1 75%, #94a3b8 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "20px 16px" : "20px 24px",
          position: "relative",
          overflow: "hidden",
          fontFamily: '"Inter", sans-serif',
        }}
      >
        {/* Efectos de fondo */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isMobile
              ? `
                radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)
              `
              : `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
              `,
            pointerEvents: "none",
          }}
        />

        {/* Card principal */}
        <div
          className="fade-in-up"
          style={{
            background: isMobile
              ? "rgba(255, 255, 255, 0.98)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: isMobile ? "24px 20px 20px 20px" : "24px 24px",
            width: "100%",
            maxWidth: isMobile ? "400px" : "500px",
            height: isMobile ? "auto" : "520px",
            minHeight: isMobile ? "520px" : "520px",
            maxHeight: isMobile ? "85vh" : "520px",
            boxShadow: isMobile
              ? "0 20px 60px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
            position: "relative",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: isMobile ? "20px" : "12px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                marginBottom: isMobile ? "10px" : "4px",
              }}
            >
              <img
                src="https://llohruvkcdcyolmwlivx.supabase.co/storage/v1/object/public/public-assets//logonokk.png"
                alt="NOKK"
                style={{
                  height: isMobile ? "28px" : "24px",
                  width: "auto",
                  opacity: 0.9,
                }}
              />
            </div>

            <p
              style={{
                fontSize: isMobile ? "13px" : "11px",
                color: "#64748b",
                margin: isMobile ? "0" : "0 0 8px 0",
                fontWeight: "400",
              }}
            >
              Configurando tu sistema
            </p>
          </div>

          {/* Progress steps */}
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
              marginBottom: isMobile ? "24px" : "12px",
            }}
          >
            <div
              style={{
                overflowY: isMobile ? "auto" : "visible",
                maxHeight: isMobile ? "60vh" : "none",
                minHeight: isMobile ? "320px" : undefined,
                paddingBottom: isMobile ? "16px" : undefined,
                WebkitOverflowScrolling: isMobile ? "touch" : undefined,
              }}
            >
            {steps.map((stepData, index) => {
              const isActive = index + 1 === step;
              const isCompleted = index + 1 < step;
              const IconComponent = stepData.icon;

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 0",
                    opacity: isActive || isCompleted ? 1 : 0.3,
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: isCompleted
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : isActive
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent
                      size={16}
                      color={isActive || isCompleted ? "white" : "#64748b"}
                      className={isActive ? "pulse" : ""}
                    />
                  </div>

                  <div style={{ textAlign: "left", flex: 1 }}>
                    <h4
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: isActive || isCompleted ? "#1e293b" : "#64748b",
                        margin: "0 0 2px 0",
                      }}
                    >
                      {stepData.title}
                    </h4>
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        margin: 0,
                        lineHeight: "1.3",
                      }}
                    >
                      {stepData.description}
                    </p>
                  </div>

                  {isActive && index + 1 < 4 && (
                    <Loader2
                      size={16}
                      className="animate-spin"
                      color="#10b981"
                    />
                  )}

                  {isCompleted && <CheckCircle size={16} color="#10b981" />}
                </div>
              );
            })}

            {/* Información de la empresa */}
            {onboardingData && (
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  marginTop: "12px",
                  textAlign: "left",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <h4
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#374151",
                    margin: "0 0 8px 0",
                  }}
                >
                  Configurando:
                </h4>
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                    margin: "0 0 2px 0",
                  }}
                >
                  {onboardingData.nombre_constructora}
                </p>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    margin: 0,
                  }}
                >
                  Admin: {onboardingData.nombre_empleado}{" "}
                  {onboardingData.apellido_empleado}
                </p>
              </div>
            )}
          </div>
          </div>

          {/* Botón de acción */}
          <div
            style={{
              display: "flex",
              gap: isMobile ? "14px" : "12px",
              justifyContent: "space-between",
              flexShrink: 0,
              paddingTop: isMobile ? "12px" : "0",
              paddingBottom: isMobile ? "16px" : "0",
              borderTop: isMobile ? "1px solid #f1f5f9" : "none",
              height: isMobile ? "52px" : "auto",
              alignItems: "center",
            }}
          >
            {step >= 4 ? (
              <button
                onClick={completarOnboarding}
                disabled={loading}
                className="nav-button-next"
                style={{
                  flex: "1 1 0%",
                  padding: isMobile ? "0 20px" : "14px 20px",
                  background: loading ? "#94a3b8" : "#1a1a1a",
                  color: "white",
                  border: "none",
                  borderRadius: isMobile ? "12px" : "10px",
                  fontSize: isMobile ? "14px" : "13px",
                  fontWeight: "500",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "none",
                  height: isMobile ? "52px" : "auto",
                  minWidth: "0",
                  maxWidth: "none",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Finalizando configuración...
                  </>
                ) : (
                  <>
                    <Rocket size={16} />
                    <span>
                      Acceder a mi Sistema
                    </span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            ) : (
              <div
                style={{
                  flex: "1 1 0%",
                  padding: isMobile ? "0 20px" : "14px 20px",
                  background: "#f8fafc",
                  color: "#64748b",
                  border: "1px solid #e2e8f0",
                  borderRadius: isMobile ? "12px" : "10px",
                  fontSize: isMobile ? "14px" : "13px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: isMobile ? "52px" : "auto",
                  textAlign: "center",
                }}
              >
                Este proceso puede tomar unos momentos...
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Onboarding;
